import { useCallback, useContext, useSyncExternalStore } from 'react';
import { PublicThemeContext } from './publicThemeContext';

/**
 * Theme switching for Atlas HUD (spec §3.1). The <html data-theme> attribute
 * holds the workspace preference; index.html sets it pre-paint from
 * localStorage so there is no flash. Default is dark — identity-first, no
 * prefers-color-scheme auto-switch. PublicLayout overrides the displayed
 * palette through context without changing that preference.
 */
export type Theme = 'dark' | 'light';

const THEME_KEY = 'ka-theme';
const listeners = new Set<() => void>();

export function getTheme(): Theme {
    return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
    const changed = document.documentElement.dataset.theme !== theme;
    document.documentElement.dataset.theme = theme;
    try {
        localStorage.setItem(THEME_KEY, theme);
    } catch {
        // Private browsing / storage denied — theme still applies for the session.
    }
    if (changed) listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

export function useTheme(): { theme: Theme; toggle: () => void } {
    const isPublicPage = useContext(PublicThemeContext);
    const theme = useSyncExternalStore(subscribe, getTheme, () => 'dark' as Theme);
    const toggle = useCallback(() => applyTheme(getTheme() === 'dark' ? 'light' : 'dark'), []);
    return { theme: isPublicPage ? 'light' : theme, toggle };
}
